import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { dict, fill, type Dict } from "@shared/i18n";
import type { Language } from "@shared/types";
import { browserLanguage, getStoredLanguage, setStoredLanguage } from "./storage";

// Language is a *per-device* setting: the TV can run English while the host's
// laptop runs German. A room also carries a default (set by its host), which
// seeds this value for anyone who has not made an explicit choice yet.

interface I18nValue {
  lang: Language;
  /** True once the user has picked a language themselves. */
  explicit: boolean;
  setLang: (lang: Language) => void;
  /** Adopt a room's default — ignored if the user already chose for themselves. */
  suggestLang: (lang: Language) => void;
  t: (key: keyof Dict, vars?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const stored = getStoredLanguage();
  const [lang, setLangState] = useState<Language>(stored ?? browserLanguage());
  const [explicit, setExplicit] = useState(stored !== null);

  const setLang = useCallback((next: Language) => {
    setLangState(next);
    setExplicit(true);
    setStoredLanguage(next);
  }, []);

  const suggestLang = useCallback((next: Language) => {
    // A room default must never override a deliberate choice.
    setLangState((cur) => (explicit ? cur : next));
  }, [explicit]);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const value = useMemo<I18nValue>(() => {
    const d = dict(lang);
    return {
      lang,
      explicit,
      setLang,
      suggestLang,
      t: (key, vars) => (vars ? fill(d[key], vars) : d[key]),
    };
  }, [lang, explicit, setLang, suggestLang]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside <I18nProvider>");
  return ctx;
}

/** Shorthand for the common case: `const t = useT()`. */
export function useT() {
  return useI18n().t;
}
